import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import { toast } from '../components/Toast';
import { ArrowLeft, Download, Upload, AlertTriangle } from 'lucide-react';
import logger from '../utils/logger';

const BusinessPaymentForm = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  
  // Base URL de l'API et routes selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = useWorkspaceBasePath();
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceIdFromUrl = searchParams.get('invoice_id');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  const [form, setForm] = useState({
    type: 'encaissement',
    client_id: '',
    invoice_id: invoiceIdFromUrl || '',
    montant: '',
    mode_paiement: 'especes',
    date_paiement: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadData = async () => {
    try {
      const [clientsRes] = await Promise.all([
        api.get(`${apiBase}/clients`)
      ]);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);

      // Si on a un invoice_id, charger la facture
      if (invoiceIdFromUrl) {
        const invoiceRes = await api.get(`${apiBase}/invoices/${invoiceIdFromUrl}`);
        if (invoiceRes.data) {
          const invoice = invoiceRes.data;
          setSelectedInvoice(invoice);
          setForm(f => ({
            ...f,
            client_id: invoice.client_id || '',
            invoice_id: invoice.id,
            montant: parseFloat(invoice.total || 0) - parseFloat(invoice.montant_paye || 0)
          }));
          
          // Charger les factures du client
          if (invoice.client_id) {
            const invoicesRes = await api.get(`${apiBase}/invoices?client_id=${invoice.client_id}&statut=envoyee,partiellement_payee`);
            setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
          }
        }
      }
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoicesForClient = async (clientId) => {
    if (!clientId) {
      setInvoices([]);
      setSelectedInvoice(null);
      return;
    }
    try {
      const res = await api.get(`${apiBase}/invoices?client_id=${clientId}&statut=envoyee,partiellement_payee`);
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.error(err);
    }
  };

  const handleClientChange = (clientId) => {
    setForm({ ...form, client_id: clientId, invoice_id: '' });
    setSelectedInvoice(null);
    loadInvoicesForClient(clientId);
  };

  const handleInvoiceChange = (invoiceId) => {
    setForm({ ...form, invoice_id: invoiceId });
    if (invoiceId) {
      const invoice = invoices.find(i => String(i.id) === String(invoiceId));
      if (invoice) {
        setSelectedInvoice(invoice);
        const reste = parseFloat(invoice.total || 0) - parseFloat(invoice.montant_paye || 0);
        setForm(f => ({ ...f, montant: reste }));
      }
    } else {
      setSelectedInvoice(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.type === 'encaissement' && !form.client_id) {
      toast.warning('Veuillez sélectionner un client');
      return;
    }
    
    if (!form.montant || parseFloat(form.montant) <= 0) {
      toast.warning('Veuillez entrer un montant valide');
      return;
    }

    setSaving(true);
    try {
      await api.post(`${apiBase}/payments`, {
        ...form,
        montant: parseFloat(form.montant)
      });
      navigate(`${routeBase}/payments`);
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const reste = selectedInvoice 
    ? parseFloat(selectedInvoice.total || 0) - parseFloat(selectedInvoice.montant_paye || 0)
    : null;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to={`${routeBase}/payments`} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
          <ArrowLeft size={16} className="inline" /> Retour
        </Link>
        <h1 className="text-2xl font-bold text-gray-800">Nouveau paiement</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl shadow p-6 space-y-6">
          {/* Type de paiement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type de paiement</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setForm({ ...form, type: 'encaissement' })}
                className={`p-4 rounded-xl border-2 transition flex items-center justify-center gap-2 ${
                  form.type === 'encaissement'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Download size={24} />
                <span className="font-medium">Encaissement</span>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, type: 'decaissement', client_id: '', invoice_id: '' })}
                className={`p-4 rounded-xl border-2 transition flex items-center justify-center gap-2 ${
                  form.type === 'decaissement'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Upload size={24} />
                <span className="font-medium">Décaissement</span>
              </button>
            </div>
          </div>

          {/* Client (pour encaissement) */}
          {form.type === 'encaissement' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
              <select
                value={form.client_id}
                onChange={(e) => handleClientChange(e.target.value)}
                className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Sélectionner un client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.nom}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Facture associée */}
          {form.type === 'encaissement' && form.client_id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Facture associée (optionnel)
              </label>
              <select
                value={form.invoice_id}
                onChange={(e) => handleInvoiceChange(e.target.value)}
                className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">-- Paiement sans facture --</option>
                {invoices.map(invoice => {
                  const invoiceReste = parseFloat(invoice.total || 0) - parseFloat(invoice.montant_paye || 0);
                  return (
                    <option key={invoice.id} value={invoice.id}>
                      {invoice.numero_facture} - Reste: {invoiceReste.toLocaleString()} XOF
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Résumé facture */}
          {selectedInvoice && (
            <div className="p-4 bg-indigo-50 rounded-xl">
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-semibold text-indigo-800">{selectedInvoice.numero_facture}</div>
                  <div className="text-sm text-indigo-600">
                    Total: {parseFloat(selectedInvoice.total).toLocaleString()} XOF
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Reste à payer</div>
                  <div className="text-xl font-bold text-red-600">{reste.toLocaleString()} XOF</div>
                </div>
              </div>
            </div>
          )}

          {/* Montant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant *</label>
            <div className="relative">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.montant}
                onChange={(e) => setForm({ ...form, montant: e.target.value })}
                placeholder="0"
                className="w-full border rounded-xl px-4 py-3 pr-16 text-lg font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">XOF</span>
            </div>
            {reste !== null && parseFloat(form.montant) > reste && (
              <p className="text-amber-600 text-sm mt-1">
                <AlertTriangle size={14} className="inline" /> Le montant dépasse le reste à payer ({reste.toLocaleString()} XOF)
              </p>
            )}
          </div>

          {/* Mode de paiement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
            <select
              value={form.mode_paiement}
              onChange={(e) => setForm({ ...form, mode_paiement: e.target.value })}
              className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="especes">Espèces</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="virement">Virement bancaire</option>
              <option value="cheque">Chèque</option>
              <option value="carte">Carte bancaire</option>
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date du paiement</label>
            <input
              type="date"
              value={form.date_paiement}
              onChange={(e) => setForm({ ...form, date_paiement: e.target.value })}
              className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Référence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Référence (optionnel)</label>
            <input
              type="text"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="N° transaction, chèque, etc."
              className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Notes additionnelles..."
              rows={3}
              className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-4">
            <Link
              to={`${routeBase}/payments`}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition text-center font-medium"
            >
              Annuler
            </Link>
            <button
              type="submit"
              disabled={saving}
              className={`flex-1 py-3 rounded-xl font-semibold transition ${
                form.type === 'encaissement'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
              } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {saving ? 'Enregistrement...' : `Enregistrer ${form.type === 'encaissement' ? 'l\'encaissement' : 'le décaissement'}`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BusinessPaymentForm;
